import React, { createContext, useContext, useState, useCallback } from 'react';
import { setFormatPrefs } from '../lib/formatPrefs';

export const UNIT_SYSTEMS = {
  METRIC: 'metric',
  IMPERIAL: 'imperial',
};

export const CURRENCIES = {
  USD: 'USD',
  AUD: 'AUD',
  COP: 'COP',
  EUR: 'EUR',
  CAD: 'CAD',
};

const SQFT_TO_SQM = 0.092903;
const SQM_TO_SQFT = 10.7639;
const FEET_TO_METERS = 0.3048;
const METERS_TO_FEET = 3.28084;

const UnitsContext = createContext(null);

export const useUnits = () => {
  const context = useContext(UnitsContext);
  if (!context) {
    throw new Error('useUnits must be used within a UnitsProvider');
  }
  return context;
};

export const UnitsProvider = ({ children }) => {
  const [unitSystem, setUnitSystemState] = useState(UNIT_SYSTEMS.IMPERIAL);
  const [currency, setCurrencyState] = useState(CURRENCIES.USD);

  const setUnitSystem = useCallback((system, options = {}) => {
    const { persist = true } = options;
    if (system === UNIT_SYSTEMS.METRIC || system === UNIT_SYSTEMS.IMPERIAL) {
      setUnitSystemState(system);
      if (persist) localStorage.setItem('unitSystem', system);
    }
  }, []);

  const setCurrency = useCallback((nextCurrency, options = {}) => {
    const { persist = true } = options;
    if (Object.values(CURRENCIES).includes(nextCurrency)) {
      setCurrencyState(nextCurrency);
      setFormatPrefs({ currency: nextCurrency });
      if (persist) localStorage.setItem('currency', nextCurrency);
    }
  }, []);

  const convertArea = useCallback((sqft, toSystem = unitSystem) => {
    if (sqft === null || sqft === undefined) return null;
    if (toSystem === UNIT_SYSTEMS.METRIC) return sqft * SQFT_TO_SQM;
    return sqft;
  }, [unitSystem]);

  const convertAreaToSqft = useCallback((value, fromSystem = unitSystem) => {
    if (value === null || value === undefined) return null;
    if (fromSystem === UNIT_SYSTEMS.METRIC) return value * SQM_TO_SQFT;
    return value;
  }, [unitSystem]);

  const convertLength = useCallback((feet, toSystem = unitSystem) => {
    if (feet === null || feet === undefined) return null;
    if (toSystem === UNIT_SYSTEMS.METRIC) return feet * FEET_TO_METERS;
    return feet;
  }, [unitSystem]);

  const convertLengthToFeet = useCallback((value, fromSystem = unitSystem) => {
    if (value === null || value === undefined) return null;
    if (fromSystem === UNIT_SYSTEMS.METRIC) return value * METERS_TO_FEET;
    return value;
  }, [unitSystem]);

  const formatArea = useCallback((sqft, decimals = 0) => {
    if (sqft === null || sqft === undefined) return '-';
    const converted = convertArea(sqft);
    const unit = unitSystem === UNIT_SYSTEMS.METRIC ? 'm²' : 'sqft';
    return `${converted.toLocaleString(undefined, { maximumFractionDigits: decimals })} ${unit}`;
  }, [convertArea, unitSystem]);

  const formatLength = useCallback((feet, decimals = 1) => {
    if (feet === null || feet === undefined) return '-';
    const converted = convertLength(feet);
    const unit = unitSystem === UNIT_SYSTEMS.METRIC ? 'm' : 'ft';
    return `${converted.toLocaleString(undefined, { maximumFractionDigits: decimals })} ${unit}`;
  }, [convertLength, unitSystem]);

  const getAreaUnit = useCallback(() => (unitSystem === UNIT_SYSTEMS.METRIC ? 'm²' : 'sqft'), [unitSystem]);
  const getLengthUnit = useCallback(() => (unitSystem === UNIT_SYSTEMS.METRIC ? 'm' : 'ft'), [unitSystem]);
  const isMetric = unitSystem === UNIT_SYSTEMS.METRIC;

  const value = {
    unitSystem,
    setUnitSystem,
    isMetric,
    convertArea,
    convertAreaToSqft,
    convertLength,
    convertLengthToFeet,
    formatArea,
    formatLength,
    getAreaUnit,
    getLengthUnit,
    UNIT_SYSTEMS,
    currency,
    setCurrency,
    CURRENCIES,
  };

  return <UnitsContext.Provider value={value}>{children}</UnitsContext.Provider>;
};

export default UnitsContext;
